import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';

const empty = {
  legal_name: '', trade_name: '', customer_group_id: '', country: '', registration_no: '', gst_vat: '', pan: '',
  default_currency: 'USD', registered_address: '', billing_address: '', primary_contact: '',
  contact_email: '', contact_phone: '', auth_signatory: '', signatory_title: '',
  msa_approver_id: '', invoice_approver_id: '', timesheet_approver_id: '', rfp_approver_id: '',
  std_unit_type: 'None',
  risk_category: 'Low', payment_score: '', credit_limit: '', security_type: '',
  board_approval: 'No', status: 'Active'
};

export default function CustomerForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [currencies, setCurrencies] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [stdUnits, setStdUnits] = useState([]);
  const isEdit = !!id;

  useEffect(() => {
    api.get('/settings/currencies').then(setCurrencies);
    api.get('/settings/customer-groups').then(setCustomerGroups);
    api.get('/auth/users').then(setUsers).catch(() => {});
    if (id) {
      api.get(`/customers/${id}`).then(c => setForm({ ...empty, ...c }));
      api.get(`/customers/${id}/std-working-units`).then(setStdUnits).catch(() => {});
    }
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addStdRow = () => setStdUnits(r => [...r, { month: '', std_units: '', remarks: '' }]);
  const setStdRow = (i, k, v) => setStdUnits(r => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const removeStdRow = (i) => setStdUnits(r => r.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    try {
      let cid = id;
      if (isEdit) await api.put(`/customers/${id}`, form);
      else {
        const r = await api.post('/customers', form);
        cid = r.customer_id;
      }
      if (form.std_unit_type !== 'None') {
        await api.put(`/customers/${cid}/std-working-units`, { rows: stdUnits });
      } else {
        await api.put(`/customers/${cid}/std-working-units`, { rows: [] });
      }
      navigate('/customers');
    } catch (e) { alert(e.message); }
  };

  return (
    <>
      <div className="page-header">
        <h1>{isEdit ? `Edit Customer: ${id}` : 'New Customer'}</h1>
        <div className="btn-group">
          <button className="btn btn-primary" onClick={handleSave}>Save Customer</button>
        </div>
      </div>
      <div className="page-content">
        <nav className="page-back-nav" aria-label="Back navigation">
          <button type="button" className="page-back-link" onClick={() => navigate('/customers')}>
            ← Back to Customer Master
          </button>
        </nav>
        <div className="card">
          <div className="card-header">Legal Identity</div>
          <div className="form-grid">
            {isEdit && <div className="form-group"><label>Customer ID</label><input value={id} readOnly /></div>}
            <div className="form-group"><label>Legal Name *</label><input value={form.legal_name} onChange={e => set('legal_name', e.target.value)} /></div>
            <div className="form-group"><label>Trade Name</label><input value={form.trade_name} onChange={e => set('trade_name', e.target.value)} /></div>
            <div className="form-group"><label>Customer Group</label>
              <select value={form.customer_group_id || ''} onChange={e => set('customer_group_id', e.target.value || null)}>
                <option value="">-- None --</option>
                {customerGroups.filter(g => g.is_active).map(g => <option key={g.id} value={g.id}>{g.group_name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Country</label><input value={form.country} onChange={e => set('country', e.target.value)} /></div>
            <div className="form-group"><label>Registration No</label><input value={form.registration_no} onChange={e => set('registration_no', e.target.value)} /></div>
            <div className="form-group"><label>GST/VAT</label><input value={form.gst_vat} onChange={e => set('gst_vat', e.target.value)} /></div>
            <div className="form-group"><label>PAN</label><input value={form.pan} onChange={e => set('pan', e.target.value)} /></div>
            <div className="form-group"><label>Default Currency</label>
              <select value={form.default_currency} onChange={e => set('default_currency', e.target.value)}>
                {currencies.filter(c => c.is_active).map(c => <option key={c.code} value={c.code}>{c.code} - {c.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Addresses</div>
          <div className="form-grid">
            <div className="form-group full-width"><label>Registered Address</label><textarea value={form.registered_address} onChange={e => set('registered_address', e.target.value)} /></div>
            <div className="form-group full-width"><label>Billing Address</label><textarea value={form.billing_address} onChange={e => set('billing_address', e.target.value)} /></div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Contacts</div>
          <div className="form-grid">
            <div className="form-group"><label>Primary Contact</label><input value={form.primary_contact} onChange={e => set('primary_contact', e.target.value)} /></div>
            <div className="form-group"><label>Email</label><input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} /></div>
            <div className="form-group"><label>Phone</label><input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} /></div>
            <div className="form-group"><label>Authorized Signatory</label><input value={form.auth_signatory} onChange={e => set('auth_signatory', e.target.value)} /></div>
            <div className="form-group"><label>Signatory Title</label><input value={form.signatory_title} onChange={e => set('signatory_title', e.target.value)} /></div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Approvers</div>
          <div className="form-grid">
            <div className="form-group"><label>MSA Approver</label>
              <select value={form.msa_approver_id || ''} onChange={e => set('msa_approver_id', e.target.value || null)}>
                <option value="">-- Select --</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Invoice Approver</label>
              <select value={form.invoice_approver_id || ''} onChange={e => set('invoice_approver_id', e.target.value || null)}>
                <option value="">-- Select --</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Timesheet Approver</label>
              <select value={form.timesheet_approver_id || ''} onChange={e => set('timesheet_approver_id', e.target.value || null)}>
                <option value="">-- Select --</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>RFP Approval</label>
              <select value={form.rfp_approver_id || ''} onChange={e => set('rfp_approver_id', e.target.value || null)}>
                <option value="">-- Select --</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Standard Working Units</div>
          <div className="form-grid">
            <div className="form-group"><label>Unit of Standard</label>
              <select value={form.std_unit_type} onChange={e => set('std_unit_type', e.target.value)}>
                <option>None</option><option>Hours</option><option>Days</option>
              </select>
            </div>
          </div>
          {form.std_unit_type !== 'None' && (
            <div style={{ marginTop: 12 }}>
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '25%' }}>Month (YYYY-MM)</th>
                      <th style={{ width: '20%' }}>Std Units ({form.std_unit_type})</th>
                      <th>Remarks</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {stdUnits.map((row, i) => (
                      <tr key={i}>
                        <td><input type="month" value={row.month} onChange={e => setStdRow(i, 'month', e.target.value)} /></td>
                        <td><input type="number" step="0.5" value={row.std_units} onChange={e => setStdRow(i, 'std_units', e.target.value)} /></td>
                        <td><input value={row.remarks || ''} onChange={e => setStdRow(i, 'remarks', e.target.value)} /></td>
                        <td><button type="button" className="btn btn-secondary btn-sm" onClick={() => removeStdRow(i)}>Remove</button></td>
                      </tr>
                    ))}
                    {stdUnits.length === 0 && (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888' }}>No rows. Click "Add Month" to begin.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={addStdRow}>+ Add Month</button>
              </div>
            </div>
          )}
        </div>

        <div className="card">
          <div className="card-header">Internal</div>
          <div className="form-grid">
            <div className="form-group"><label>Risk Category</label>
              <select value={form.risk_category} onChange={e => set('risk_category', e.target.value)}>
                <option>Low</option><option>Medium</option><option>High</option>
              </select>
            </div>
            <div className="form-group"><label>Payment Score</label><input type="number" step="0.1" value={form.payment_score} onChange={e => set('payment_score', e.target.value)} /></div>
            <div className="form-group"><label>Credit Limit</label><input type="number" value={form.credit_limit} onChange={e => set('credit_limit', e.target.value)} /></div>
            <div className="form-group"><label>Security Type</label><input value={form.security_type || ''} onChange={e => set('security_type', e.target.value)} /></div>
            <div className="form-group"><label>Board Approval</label>
              <select value={form.board_approval} onChange={e => set('board_approval', e.target.value)}>
                <option>No</option><option>Yes</option>
              </select>
            </div>
            <div className="form-group"><label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                <option>Active</option><option>Inactive</option><option>Suspended</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
